import { ChangeEvent, useEffect, useState } from 'react'
import RightDrawer from 'baby-design/components/RightDrawer/RightDrawer'

import css from './RegItemFilterDrawer.styles.scss'
import { Button, Checkbox, RadioButton } from 'baby-design'
import Collapsible from '../Collapsible/Collapsible'
import classNames from 'classnames'
import NiceModal, { useModal } from '@ebay/nice-modal-react'

export type AvailabilityFilterOption =
  | 'all'
  | 'available'
  | 'reservedAndPurchased'
  | 'groupGift'

export type PriceFilterOption =
  | 'all'
  | 'under25'
  | '25to50'
  | '50to100'
  | '100to200'
  | 'over200'

export interface RegItemFilters {
  availability: AvailabilityFilterOption
  mustHavesOnly: boolean
  price: PriceFilterOption
  stores: Record<string, boolean>
}

interface RegItemFilterDrawerProps {
  filters: RegItemFilters
  setFilters: (filters: RegItemFilters) => void
}

export default NiceModal.create(
  ({ filters, setFilters }: RegItemFilterDrawerProps) => {
    const modal = useModal()

    const {
      availability: availabilityFilter,
      mustHavesOnly: mustHavesOnlyFilter,
      price: priceFilter,
      stores: storesFilter,
    } = filters

    const [availabilitySectionIsCollapsed, setAvailabilitySectionIsCollapsed] =
      useState(false)
    const [mustHavesSectionIsCollapsed, setMustHavesSectionIsCollapsed] =
      useState(false)
    const [priceSectionIsCollapsed, setPriceSectionIsCollapsed] =
      useState(false)
    const [storesSectionIsCollapsed, setStoresSectionIsCollapsed] =
      useState(false)

    useEffect(() => {
      if (modal.visible) {
        return
      }
      setAvailabilitySectionIsCollapsed(false)
      setMustHavesSectionIsCollapsed(false)
      setPriceSectionIsCollapsed(false)
      setStoresSectionIsCollapsed(false)
    }, [modal.visible])

    const handleAvailabilityFilterChange = (
      e: ChangeEvent<HTMLInputElement>
    ) => {
      if (e.target.value === availabilityFilter) return

      setFilters({
        ...filters,
        availability: e.target.value as AvailabilityFilterOption,
      })
    }

    const handleMustHavesOnlyFilterChange = (
      e: ChangeEvent<HTMLInputElement>
    ) => {
      setFilters({
        ...filters,
        mustHavesOnly: e.target.checked,
      })
    }

    const handlePriceFilterChange = (value: PriceFilterOption) => {
      if (value === priceFilter) return

      setFilters({
        ...filters,
        price: value,
      })
    }

    // All true and all false will be treated the same way
    const filterByAllStores =
      Object.keys(filters.stores).every(
        (storeDisplayName) => filters.stores[storeDisplayName]
      ) ||
      Object.keys(filters.stores).every(
        (storeDisplayName) => !filters.stores[storeDisplayName]
      )

    const handleAllStoresFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
      // Do nothing when clicking this checkbox if it's already checked
      if (!e.target.checked) return

      const updatedStoresFilter = Object.keys(filters.stores).reduce<
        Record<string, boolean>
      >((acc, storeDisplayName) => {
        acc[storeDisplayName] = e.target.checked
        return acc
      }, {})
      setFilters({
        ...filters,
        stores: updatedStoresFilter,
      })
    }

    const handleStoresFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
      const newStoresFilter = { ...storesFilter }
      if (filterByAllStores) {
        Object.keys(storesFilter).forEach((storeDisplayName) => {
          newStoresFilter[storeDisplayName] =
            storeDisplayName === e.target.value
        })
      } else {
        newStoresFilter[e.target.value] = e.target.checked
      }

      setFilters({
        ...filters,
        stores: newStoresFilter,
      })
    }

    const sortedStoreKeys = Object.keys(filters.stores).sort((a, b) => {
      const storeA = a.toLowerCase()
      const storeB = b.toLowerCase()
      if (storeA < storeB) return -1
      if (storeA > storeB) return 1
      return 0
    })

    const availabilityOptions: Array<{
      value: AvailabilityFilterOption
      label: string
    }> = [
      { value: 'all', label: 'All Items' },
      { value: 'available', label: 'Available Gifts' },
      { value: 'reservedAndPurchased', label: 'Reserved and Purchased Gifts' },
      { value: 'groupGift', label: 'Group Gift' },
    ]

    const priceOptions: Array<{ value: PriceFilterOption; label: string }> = [
      { value: 'all', label: 'Any Price' },
      { value: 'under25', label: 'Under $25' },
      { value: '25to50', label: '$25 to $50' },
      { value: '50to100', label: '$50 to $100' },
      { value: '100to200', label: '$100 to $200' },
      { value: 'over200', label: 'Over $200' },
    ]

    const closeDrawer = () => modal.hide()

    return (
      <RightDrawer
        className={css.RegItemFilterDrawer}
        handleDismount={modal.remove}
        handleClose={closeDrawer}
        isOpen={modal.visible}
        title="Filters"
      >
        <Collapsible
          contentMaxHeightClassName={
            css['RegItemFilterDrawer__availabilitySection--maxHeight']
          }
          handleCollapse={() =>
            setAvailabilitySectionIsCollapsed(!availabilitySectionIsCollapsed)
          }
          isCollapsed={availabilitySectionIsCollapsed}
          title="Availability"
        >
          <div className={css.RegItemFilterDrawer__selectableGroup}>
            {availabilityOptions.map(({ value, label }) => (
              <div className={css.RegItemFilterDrawer__selectable} key={value}>
                <RadioButton
                  checked={availabilityFilter === value}
                  id={`availability_${value}`}
                  name="availability"
                  onChange={handleAvailabilityFilterChange}
                  value={value}
                  size="md"
                />
                <label htmlFor={`availability_${value}`}>{label}</label>
              </div>
            ))}
          </div>
        </Collapsible>
        <Collapsible
          contentMaxHeightClassName={
            css['RegItemFilterDrawer__mustHavesSection--maxHeight']
          }
          handleCollapse={() =>
            setMustHavesSectionIsCollapsed(!mustHavesSectionIsCollapsed)
          }
          isCollapsed={mustHavesSectionIsCollapsed}
          title="Must Haves"
        >
          <div className={css.RegItemFilterDrawer__selectableGroup}>
            <div className={css.RegItemFilterDrawer__selectable}>
              <Checkbox
                checked={mustHavesOnlyFilter}
                id="mustHavesOnly"
                onChange={handleMustHavesOnlyFilterChange}
                size="md"
              />
              <label htmlFor="mustHavesOnly">Show Must Haves Only</label>
            </div>
          </div>
        </Collapsible>
        <Collapsible
          contentMaxHeightClassName={
            css['RegItemFilterDrawer__priceSection--maxHeight']
          }
          handleCollapse={() =>
            setPriceSectionIsCollapsed(!priceSectionIsCollapsed)
          }
          isCollapsed={priceSectionIsCollapsed}
          title="Price"
        >
          <div className={css.RegItemFilterDrawer__buttonGrid}>
            {priceOptions.map(({ value, label }) => (
              <Button
                className={classNames(css.RegItemFilterDrawer__button, {
                  [css['RegItemFilterDrawer__button--active']]:
                    priceFilter === value,
                })}
                key={value}
                onClick={() => handlePriceFilterChange(value)}
                size="md"
                variant="tertiary"
              >
                {label}
              </Button>
            ))}
          </div>
        </Collapsible>
        <Collapsible
          contentMaxHeightClassName={
            css['RegItemFilterDrawer__storesSection--maxHeight']
          }
          handleCollapse={() =>
            setStoresSectionIsCollapsed(!storesSectionIsCollapsed)
          }
          isCollapsed={storesSectionIsCollapsed}
          title="Stores"
        >
          <div className={css.RegItemFilterDrawer__selectableGroup}>
            <div className={css.RegItemFilterDrawer__selectable}>
              <Checkbox
                checked={filterByAllStores}
                id="stores_all"
                onChange={handleAllStoresFilterChange}
                size="md"
              />
              <label htmlFor="stores_all">All Stores</label>
            </div>
            {sortedStoreKeys.map((storeDisplayName) => {
              const id = `store_${storeDisplayName}`
              const isChecked = filterByAllStores
                ? false
                : storesFilter[storeDisplayName]
              return (
                <div
                  className={css.RegItemFilterDrawer__selectable}
                  key={storeDisplayName}
                >
                  <Checkbox
                    checked={isChecked}
                    id={id}
                    onChange={handleStoresFilterChange}
                    size="md"
                    value={storeDisplayName}
                  />
                  <label htmlFor={id}>{storeDisplayName}</label>
                </div>
              )
            })}
          </div>
        </Collapsible>
      </RightDrawer>
    )
  }
)
